import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { SharedModule } from '@aiwyn/shared';
import { CrlDisplaySection, CrlRequestItem } from '../../../../models';
import { CrlItemRowComponent } from '../crl-item-row/crl-item-row.component';

/**
 * The grouped section list for the CRL list view (REQ-4).
 *
 * Receives `displaySections` already projected by the selector (items split
 * into open vs finished, sections ordered with completed ones last). This
 * component owns only local UI collapse state:
 *  - each section header toggles the whole section open/closed,
 *  - each section's finished items live in a collapsible area at its bottom,
 *    collapsed by default.
 * It also renders the "completed sections" divider before the first fully
 * complete section. Item clicks bubble up via `itemSelected`.
 */
@Component({
  selector: 'cxp-crl-section-list',
  templateUrl: './crl-section-list.component.html',
  styleUrls: ['./crl-section-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, CrlItemRowComponent],
})
export class CrlSectionListComponent {
  readonly sections = input.required<CrlDisplaySection[]>();
  /** True while bulk-actions selection mode is active (REQ-11). */
  readonly selectionMode = input<boolean>(false);
  /** Ids of currently-selected items (drives each row's checkbox). */
  readonly selectedItemIds = input<ReadonlySet<number>>(new Set<number>());
  /** Ids whose undo call is in flight (disables those rows' Undo link). */
  readonly undoPendingItemIds = input<ReadonlySet<number>>(new Set<number>());
  readonly itemSelected = output<CrlRequestItem>();
  readonly commentsSelected = output<CrlRequestItem>();
  /** Bubbles a row's selection-checkbox toggle up to the container. */
  readonly itemSelectionToggled = output<CrlRequestItem>();
  /** Bubbles a row's "Undo" on its marked-as-N/A add-on up to the container. */
  readonly itemNotApplicableUndone = output<CrlRequestItem>();

  isItemSelected(itemId: number): boolean {
    return this.selectedItemIds().has(itemId);
  }

  isUndoPending(itemId: number): boolean {
    return this.undoPendingItemIds().has(itemId);
  }

  /** Section ids the user has collapsed (open by default). */
  private readonly collapsedSectionIds = signal<ReadonlySet<number>>(new Set());
  /** Section ids whose finished-items area the user has expanded (collapsed by default). */
  private readonly expandedFinishedIds = signal<ReadonlySet<number>>(new Set());

  /** Ordinal of the first complete section — used to place the divider once. */
  protected readonly firstCompleteSectionId = computed(() => this.sections().find((section) => section.isComplete)?.id ?? null);

  isSectionCollapsed(sectionId: number): boolean {
    return this.collapsedSectionIds().has(sectionId);
  }

  isFinishedExpanded(sectionId: number): boolean {
    return this.expandedFinishedIds().has(sectionId);
  }

  toggleSection(sectionId: number): void {
    this.collapsedSectionIds.update((set) => toggle(set, sectionId));
  }

  toggleFinished(sectionId: number): void {
    this.expandedFinishedIds.update((set) => toggle(set, sectionId));
  }

  onItemSelected(item: CrlRequestItem): void {
    this.itemSelected.emit(item);
  }

  protected onItemCommentsSelected(item: CrlRequestItem): void {
    this.commentsSelected.emit(item);
  }

  onItemSelectionToggled(item: CrlRequestItem): void {
    this.itemSelectionToggled.emit(item);
  }

  onItemNotApplicableUndone(item: CrlRequestItem): void {
    this.itemNotApplicableUndone.emit(item);
  }
}

/** Returns a new set with `id` toggled — keeps signal updates immutable. */
function toggle(set: ReadonlySet<number>, id: number): ReadonlySet<number> {
  const next = new Set(set);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}
